import type { PaginatedResponseDto } from 'framework/api';
import { canCreatePositionFillRequest, type PositionSlot, type PositionSlotListItem } from 'domain/shared/positionSlot';
import { usePeopleManagementApi } from 'infrastructure/api';
import { ASC } from 'framework/list';

const POSITION_SLOTS_SEARCH_URL = '/position-slots/search';
const PAGE_SIZE = 200;
const OFFICE_FILTER_SOURCE = 'specification:positionSlotByOfficeSpecification';

type SearchCondition = Record<string, string | number | boolean | number[]>;
export type MatchingPositionSlotsFilter = Record<string, SearchCondition>;

const exactValueCondition = (value: string | null): SearchCondition =>
  value === null ? { NULL: true } : { eq: value };

export const buildMatchingPositionSlotsFilter = (positionSlot: PositionSlotListItem): MatchingPositionSlotsFilter => {
  const filter: MatchingPositionSlotsFilter = {
    specializationId: { eq: positionSlot.specializationId },
    technologyId: exactValueCondition(positionSlot.technologyId),
    competencyLevel: exactValueCondition(positionSlot.competencyLevel),
    businessFunctionId: { eq: positionSlot.businessFunctionId },
    workerType: { eq: positionSlot.workerType },
    workRateType: { eq: positionSlot.workRateType },
    lifecycle: { eq: positionSlot.lifecycle },
    positionFulfillmentRequestId: { NULL: true },
  };

  if (positionSlot.officeIds.length) {
    filter[OFFICE_FILTER_SOURCE] = { in: positionSlot.officeIds };
  }

  return filter;
};

const hasExactlyTheSameOffices = (positionSlot: PositionSlot, officeIds: number[]) => {
  if (positionSlot.officeIds.length !== officeIds.length) return false;

  const expectedOfficeIds = new Set(officeIds);
  return positionSlot.officeIds.every(officeId => expectedOfficeIds.has(officeId));
};

const useMatchingPositionsService = () => {
  const { sendPostRequest } = usePeopleManagementApi();

  const getMatchingPositionSlots = async (positionSlot: PositionSlotListItem): Promise<PositionSlot[]> => {
    const filter = buildMatchingPositionSlotsFilter(positionSlot);

    const response = await sendPostRequest(POSITION_SLOTS_SEARCH_URL, {
      pageNumber: 0,
      pageSize: PAGE_SIZE,
      filter,
      sort: { id: ASC },
    });
    const page = (await response.json()) as PaginatedResponseDto<PositionSlot>;

    return (page.result || []).filter(
      candidate =>
        candidate.id !== positionSlot.id &&
        hasExactlyTheSameOffices(candidate, positionSlot.officeIds) &&
        canCreatePositionFillRequest(candidate),
    );
  };

  return { getMatchingPositionSlots };
};

export default useMatchingPositionsService;
